import React, { useCallback, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import rehypeKatex from "rehype-katex";
import remarkMath from "remark-math";
import useBreakpointRanges from "utilities/hooks/use-breakpoint-ranges";

interface MathPopupProps {
    children: React.ReactNode;
    originalContent?: string;
}

const MathWithPopup: React.FC<MathPopupProps> = ({
    children,
    originalContent,
}) => {
    const { inMobileView } = useBreakpointRanges();
    const [showPopup, setShowPopup] = useState(false);
    const mathRef = useRef<HTMLDivElement>(null);

    // Fallback: extract from DOM if originalContent not provided
    const getLatexContent = useCallback(() => {
        if (originalContent) {
            return originalContent;
        }

        if (mathRef.current) {
            const annotation = mathRef.current.querySelector(
                'annotation[encoding="application/x-tex"]'
            );
            if (annotation?.textContent) {
                return `$${annotation.textContent}$`;
            }
        }

        return "";
    }, [originalContent]);

    const handleOpen = useCallback(
        (e: React.MouseEvent | React.TouchEvent) => {
            const latexContent = getLatexContent();
            if (inMobileView && latexContent) {
                e.preventDefault();
                e.stopPropagation();
                setShowPopup(true);
            }
        },
        [inMobileView, getLatexContent]
    );

    const handleClose = useCallback(
        (e: React.MouseEvent | React.TouchEvent) => {
            e.preventDefault();
            e.stopPropagation();
            setShowPopup(false);
        },
        []
    );

    const handlePopupClick = useCallback(
        (e: React.MouseEvent | React.TouchEvent) => {
            e.preventDefault();
            e.stopPropagation();
        },
        []
    );

    if (!inMobileView) {
        return <>{children}</>;
    }

    return (
        <>
            <div ref={mathRef} className="math-with-popup" onClick={handleOpen}>
                {children}
            </div>
            {showPopup && (
                <div className="math-popup-overlay" onClick={handleClose}>
                    <div className="math-popup" onClick={handlePopupClick}>
                        <ReactMarkdown
                            remarkPlugins={[remarkMath]}
                            rehypePlugins={[rehypeKatex]}>
                            {getLatexContent()}
                        </ReactMarkdown>
                    </div>
                </div>
            )}
        </>
    );
};

export { MathWithPopup };
